// The reader's accuracy over the game (`../accuracy.ts`): each of the
// reader's moves scored from the scan's score before and after it, from the
// reader's side, then averaged. Moves the scan has no score around are left out.
// The same per-move accuracies, split by stage (the reader's moves after the
// stage's `fromPly` up to its `toPly`) and combined by the same rule, give each
// stage's accuracy; a stage with fewer than `STAGE_ACCURACY_MIN_MOVES` of them
// has none.
import { type FactExtractor, type ScannedGame } from '../extractor.ts';
import { STAGE_ACCURACY_MIN_MOVES, type GameFacts, type Phase } from '@chess/api-model';
import { gameAccuracy, moveAccuracy } from '../accuracy.ts';

const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];

export const accuracy: FactExtractor = {
  id: 'accuracy',
  extract(game: ScannedGame, facts: GameFacts): void {
    const moves: number[] = [];
    const byPly = new Map<number, number>();
    for (let ply = 1; ply <= game.moves.length; ply++) {
      if (!game.isReaders(ply)) continue;
      const before = game.pawnsAt(ply - 1);
      const after = game.pawnsAt(ply);
      if (before === undefined || after === undefined) continue;
      const value = moveAccuracy(before, after);
      moves.push(value);
      byPly.set(ply, value);
    }
    const value = gameAccuracy(moves);
    if (value !== undefined) facts.accuracy = value;

    const stages: Partial<Record<Phase, number>> = {};
    for (const phase of PHASES) {
      const { fromPly, toPly } = game.range(phase);
      const inStage: number[] = [];
      for (const [ply, move] of byPly) if (ply > fromPly && ply <= toPly) inStage.push(move);
      if (inStage.length < STAGE_ACCURACY_MIN_MOVES) continue;
      const stage = gameAccuracy(inStage);
      if (stage !== undefined) stages[phase] = stage;
    }
    if (value !== undefined) facts.stageAccuracy = stages;
  },
};
